/**
 * สร้างข้อมูลสินค้าใหม่ — `POST /v1/products`
 *
 * ฟอร์มนี้ส่ง **เฉพาะ** ฟิลด์ที่ contract ระบุไว้ใน body: `product_code`,
 * `name`, `product_type`, `base_uom` และ `plant_data` อย่างน้อยหนึ่งรายการ
 *
 * สิ่งที่จงใจ "ไม่มี" ในฟอร์มนี้ และเหตุผล:
 * - **ไม่มีช่องสถานะ** — record ใหม่เป็น `DRAFT` เสมอ client ส่ง `status` ไม่ได้
 * - **ไม่มีช่องผู้สร้าง/ผู้ส่ง/ผู้อนุมัติ** — เซิร์ฟเวอร์เติมจาก auth context
 *   เท่านั้น ถ้า client ระบุผู้อนุมัติเองได้ D-010 ก็ไร้ความหมายทันที
 *   (contract ตอบ `400` ให้ request ที่เอ่ยถึงฟิลด์เหล่านี้)
 * - **ไม่มีตัวเลือก PULL** — เฟส 1 ไม่มี Pull flow (D-034) แต่หน้ารายละเอียด
 *   ยังแสดงค่า `PULL` ได้ถ้าเซิร์ฟเวอร์ส่งมา: การแสดงผลกับการรับ input
 *   เป็นคนละปัญหา
 *
 * ช่องที่เป็น `NUMERIC` (`safety_stock`, `unit_cost`) เป็น `type="number"` เพื่อให้มี
 * ลูกศรเพิ่ม/ลดจริง (D-135 ข้อ 4) แต่ **ค่ายังเป็น string ตามที่พิมพ์ ไม่แปลง
 * float** — ตรวจก่อนส่งด้วย regex บนสตริงเอง (ไม่ใช้ `Number()`) เพื่อไม่ให้
 * ความละเอียดของ `NUMERIC(18,4)` หายไปเงียบ ๆ และเพื่อปฏิเสธรูปยกกำลัง (`1e3`)
 * ที่ `type="number"` ของเบราว์เซอร์ยอมรับโดยไม่เตือน
 *
 * `product_type`/`base_uom`/`plant_code` เป็น `<select>` จากทะเบียนจริง (D-135):
 * ประเภทสินค้าและหน่วยนับมีปุ่ม '+' เปิด `ProductRegisterModal` ส่วนโรงงานไม่มี
 * เพราะระบบไม่มีเส้นสร้างโรงงาน (ดูคอมเมนต์ที่ช่องนั้น)
 */

import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { ProductFailureNotice } from './ProductFailureNotice';
import { ProductRegisterModal, type ProductRegisterKind } from './ProductRegisterModal';
import { ProductSteelFields } from './ProductSteelFields';
import { EMPTY_STEEL_TEXTS, steelProblems, type SteelTexts } from './productView';
import { InfoHint } from '../components/InfoHint';
import { selectablePlants } from '../productionOrders/productionOrderView';
import { createProduct, fetchProductTypes, fetchUoms, type ProductFailure } from '../../lib/products/client';
import {
  CREATABLE_REPLENISHMENT_MODES,
  type CreateProductBody,
  type CreateProductPlantInput,
  type Product,
} from '../../lib/products/types';
import type { PermissionsState } from '../../lib/permissions/types';

export interface ProductCreateViewProps {
  readonly apiBaseUrl: string;
  readonly fetchImpl?: typeof fetch;
  readonly onCreated: (product: Product) => void;
  /** ใช้หา `plant_scope` (เลือกโรงงานได้) และส่งต่อให้ `ProductRegisterModal` ตัดสินสิทธิ์เอง */
  readonly permissions: PermissionsState;
}

interface CreateFormState {
  readonly product_code: string;
  readonly name: string;
  readonly model: string;
  readonly product_type: string;
  readonly base_uom: string;
  readonly plant_code: string;
  readonly valuation_class: string;
  readonly planned_delivery_time: string;
  readonly lot_size_procedure: string;
  readonly safety_stock: string;
  readonly unit_cost: string;
  /** น้ำหนักต่อชิ้นและข้อมูลเหล็ก (D-165) — ไม่บังคับ */
  readonly steel: SteelTexts;
}

function emptyForm(initialPlantCode: string): CreateFormState {
  return {
    product_code: '',
    name: '',
    model: '',
    product_type: '',
    base_uom: '',
    plant_code: initialPlantCode,
    // ค่าเริ่มต้น '0' (D-141) — ต่างจากสามช่องด้านล่าง: ช่องนี้ยังบังคับกรอก ล้างว่างไม่ได้
    valuation_class: '0',
    // ค่าเริ่มต้นของฟอร์ม (D-138) — ยังแก้ได้และล้างให้ว่างได้เหมือนเดิม
    planned_delivery_time: '1',
    lot_size_procedure: '',
    safety_stock: '100',
    unit_cost: '0.01',
    steel: EMPTY_STEEL_TEXTS,
  };
}

type SubmitState =
  | { readonly status: 'idle' }
  | { readonly status: 'submitting' }
  | { readonly status: 'invalid-form'; readonly problems: readonly string[] }
  | ProductFailure;

/** เลขจำนวนเต็มไม่ติดลบเท่านั้น — `planned_delivery_time` เป็น INT ไม่ใช่ NUMERIC */
const INTEGER_PATTERN = /^\d+$/;

/** ไม่ติดลบ ทศนิยมไม่เกิน 4 ตำแหน่ง ไม่มีรูปยกกำลัง — ตรวจตรงกับ `NUMERIC(18,4)` (D-135 ข้อ 4) */
const DECIMAL_UP_TO_4_PATTERN = /^\d+(\.\d{1,4})?$/;

type BuildResult =
  | { readonly ok: true; readonly body: CreateProductBody }
  | { readonly ok: false; readonly problems: readonly string[] };

/**
 * ช่องว่างแปลว่า "ไม่ส่ง field นี้" ไม่ใช่ "ส่งค่าว่าง"
 *
 * `undefined` ถูก `serializeCreateProductBody()` ตัดทิ้งตอนประกอบ request
 * ค่าที่ผู้ใช้ไม่ได้กรอกจึงหายไปจาก body ทั้งคีย์ แทนที่จะกลายเป็นสตริงว่างที่
 * เซิร์ฟเวอร์ต้องมาเดาความหมายเอง
 */
function trimmedOrOmitted(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

function steelFormOrOmitted(value: string): 'SHEET' | 'COIL' | undefined {
  const trimmed = value.trim();
  return trimmed === 'SHEET' || trimmed === 'COIL' ? trimmed : undefined;
}

/**
 * ประกอบ body จากฟอร์ม — คีย์ที่ออกไปถูกเขียนไว้ตรงนี้ทีละตัว
 *
 * ไม่ spread state ของฟอร์มทั้งก้อนโดยเจตนา: การ spread คือวิธีที่ฟิลด์ซึ่ง
 * เซิร์ฟเวอร์เป็นเจ้าของจะหลุดออกไปกับ request ในวันที่มีคนเพิ่มช่องใหม่
 */
function buildBody(form: CreateFormState): BuildResult {
  const problems: string[] = [];
  const required: ReadonlyArray<readonly [Exclude<keyof CreateFormState, 'steel'>, string]> = [
    ['product_code', 'รหัสสินค้า'],
    ['name', 'ชื่อสินค้า'],
    ['product_type', 'ประเภทสินค้า'],
    ['base_uom', 'หน่วยนับหลัก'],
    ['plant_code', 'รหัสโรงงาน'],
  ];
  for (const [field, label] of required) {
    if (form[field].trim() === '') {
      problems.push(`ต้องกรอก${label}`);
    }
  }

  // D-141: ยังบังคับกรอกเหมือนเดิม การมีค่าเริ่มต้น '0' ไม่ได้ทำให้ช่องนี้ไม่บังคับ
  if (form.valuation_class.trim() === '') {
    problems.push('ต้องกรอก Valuation class');
  }

  const leadTime = form.planned_delivery_time.trim();
  if (leadTime !== '' && !INTEGER_PATTERN.test(leadTime)) {
    problems.push('Lead time ต้องเป็นจำนวนเต็มไม่ติดลบ (หน่วยเป็นวัน)');
  }

  if (form.safety_stock !== '' && !DECIMAL_UP_TO_4_PATTERN.test(form.safety_stock)) {
    problems.push(
      'สต๊อกปลอดภัยต้องเป็นตัวเลขไม่ติดลบ ทศนิยมไม่เกิน 4 ตำแหน่ง และห้ามใช้เลขยกกำลัง (เช่น 1e3)',
    );
  }
  if (form.unit_cost !== '' && !DECIMAL_UP_TO_4_PATTERN.test(form.unit_cost)) {
    problems.push(
      'ต้นทุนต่อหน่วยต้องเป็นตัวเลขไม่ติดลบ ทศนิยมไม่เกิน 4 ตำแหน่ง และห้ามใช้เลขยกกำลัง (เช่น 1e3)',
    );
  }

  problems.push(...steelProblems(form.steel));

  if (problems.length > 0) {
    return { ok: false, problems };
  }

  const plant: CreateProductPlantInput = {
    plant_code: form.plant_code.trim(),
    // เฟส 1 ส่งได้ค่าเดียว และชนิดข้อมูลก็ยอมรับค่าเดียว (D-034)
    default_replenishment_mode: CREATABLE_REPLENISHMENT_MODES[0],
    valuation_class: trimmedOrOmitted(form.valuation_class),
    lot_size_procedure: trimmedOrOmitted(form.lot_size_procedure),
    planned_delivery_time: leadTime === '' ? undefined : Number(leadTime),
    // ⚠️ ไม่ trim และไม่แปลงชนิด: ค่าที่ผู้ใช้พิมพ์ต้องถึงเซิร์ฟเวอร์เหมือนเดิมทุกตัวอักษร
    safety_stock: form.safety_stock === '' ? undefined : form.safety_stock,
    unit_cost: form.unit_cost === '' ? undefined : form.unit_cost,
  };

  return {
    ok: true,
    body: {
      product_code: form.product_code.trim(),
      name: form.name.trim(),
      // ไม่กรอก = ไม่ส่ง key นี้เลย (D-107 ข้อ 1) — สินค้าส่วนใหญ่ไม่มีรุ่น/โมเดล
      // และนั่นเป็นเรื่องปกติ ไม่ใช่ข้อมูลที่ขาด
      model: trimmedOrOmitted(form.model),
      product_type: form.product_type.trim(),
      base_uom: form.base_uom.trim(),
      kg_per_piece: trimmedOrOmitted(form.steel.kg_per_piece),
      material_spec: trimmedOrOmitted(form.steel.material_spec),
      material_form: steelFormOrOmitted(form.steel.material_form),
      thickness_mm: trimmedOrOmitted(form.steel.thickness_mm),
      width_mm: trimmedOrOmitted(form.steel.width_mm),
      length_mm: trimmedOrOmitted(form.steel.length_mm),
      plant_data: [plant],
    },
  };
}

interface SelectOption {
  readonly code: string;
  readonly name: string;
}

type RegistryOptionsState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly options: readonly SelectOption[] }
  | ProductFailure;

export function ProductCreateView({
  apiBaseUrl,
  fetchImpl,
  onCreated,
  permissions,
}: ProductCreateViewProps) {
  const { pathname } = useLocation();
  const plants = selectablePlants(permissions);
  const [form, setForm] = useState<CreateFormState>(() =>
    emptyForm(plants.length === 1 ? plants[0] : ''),
  );
  const [submit, setSubmit] = useState<SubmitState>({ status: 'idle' });

  const [productTypes, setProductTypes] = useState<RegistryOptionsState>({ status: 'loading' });
  const [productTypesReloadToken, setProductTypesReloadToken] = useState(0);
  const [uoms, setUoms] = useState<RegistryOptionsState>({ status: 'loading' });
  const [uomsReloadToken, setUomsReloadToken] = useState(0);
  const [registerModalKind, setRegisterModalKind] = useState<ProductRegisterKind | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setProductTypes((previous) => (previous.status === 'ready' ? previous : { status: 'loading' }));
    void fetchProductTypes(
      { status: 'ACTIVE' },
      { apiBaseUrl, fetchImpl, signal: controller.signal },
    ).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      if (result.status !== 'ready') {
        setProductTypes(result);
        return;
      }
      setProductTypes({
        status: 'ready',
        options: result.page.items.map((entry) => ({ code: entry.type_code, name: entry.name })),
      });
    });
    return () => {
      active = false;
      controller.abort();
    };
  }, [apiBaseUrl, fetchImpl, productTypesReloadToken]);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setUoms((previous) => (previous.status === 'ready' ? previous : { status: 'loading' }));
    void fetchUoms({ apiBaseUrl, fetchImpl, signal: controller.signal }).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      if (result.status !== 'ready') {
        setUoms(result);
        return;
      }
      setUoms({
        status: 'ready',
        options: result.page.items.map((entry) => ({ code: entry.uom_code, name: entry.name })),
      });
    });
    return () => {
      active = false;
      controller.abort();
    };
  }, [apiBaseUrl, fetchImpl, uomsReloadToken]);

  const update = (field: keyof CreateFormState, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const updateSteel = (field: keyof SteelTexts, value: string) => {
    setForm((current) => ({ ...current, steel: { ...current.steel, [field]: value } }));
  };

  const handleRegisterChanged = () => {
    if (registerModalKind === 'type') {
      setProductTypesReloadToken((token) => token + 1);
    } else if (registerModalKind === 'uom') {
      setUomsReloadToken((token) => token + 1);
    }
  };

  const handleRegisterCreated = (code: string) => {
    if (registerModalKind === 'type') {
      update('product_type', code);
    } else if (registerModalKind === 'uom') {
      update('base_uom', code);
    }
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submit.status === 'submitting') {
      return;
    }

    const built = buildBody(form);
    if (!built.ok) {
      setSubmit({ status: 'invalid-form', problems: built.problems });
      return;
    }

    setSubmit({ status: 'submitting' });
    const result = await createProduct(built.body, { apiBaseUrl, fetchImpl });
    if (result.status === 'ready') {
      onCreated(result.product);
      return;
    }
    setSubmit(result);
  };

  return (
    <div className="cap-page" data-testid="product-create">
      <div className="cap-page__head">
        <div>
          <h1 className="cap-page__title">
            สร้างข้อมูลสินค้าใหม่
            <InfoHint
              label="เกี่ยวกับการบันทึกสินค้าที่สร้างใหม่"
              text="บันทึกเป็นสถานะ “ร่าง (DRAFT)” เสมอ — ส่งอนุมัติได้จากหน้ารายละเอียดของรายการที่บันทึกแล้ว"
            />
          </h1>
        </div>
        <div className="cap-page__actions">
          <Link className="cap-button cap-button--ghost" to={{ pathname, search: '' }}>
            กลับไปรายการสินค้า
          </Link>
        </div>
      </div>

      {submit.status === 'invalid-form' ? (
        <div
          className="cap-banner cap-banner--warning"
          role="status"
          data-testid="product-create-invalid"
        >
          <strong>ยังส่งไม่ได้ — ข้อมูลไม่ครบ</strong>
          <span>{submit.problems.join(' · ')}</span>
        </div>
      ) : null}

      {/* แจกแจงสถานะที่ "ไม่ใช่ความล้มเหลวจากเซิร์ฟเวอร์" ออกไป แล้วให้ TypeScript
          เป็นผู้ยืนยันว่าที่เหลือคือ `ProductFailure` จริง ไม่ใช้ cast */}
      {submit.status === 'idle' ||
      submit.status === 'submitting' ||
      submit.status === 'invalid-form' ? null : (
        <ProductFailureNotice failure={submit} context="create" />
      )}

      <form className="cap-form" onSubmit={(event) => void onSubmit(event)} noValidate>
        <section className="cap-card cap-card--form" aria-label="ข้อมูลทั่วไป">
          <div className="cap-card__head">
            <h2 className="cap-card__title">
              ข้อมูลทั่วไป
              <InfoHint label="เกี่ยวกับขอบเขตของข้อมูลทั่วไป" text="ใช้ร่วมกันทุกโรงงาน" />
            </h2>
          </div>
          <div className="cap-form__grid">
            <label className="cap-field">
              <span className="cap-field__label">รหัสสินค้า (product_code)</span>
              <input
                className="cap-input"
                name="product_code"
                value={form.product_code}
                onChange={(event) => update('product_code', event.target.value)}
                placeholder="RM-STEEL-8MM"
                required
              />
              {/* InfoHint อยู่หลัง input เสมอ — ถ้าอยู่ก่อน `label.control` (และ
                  `getByLabelText`) จะจับปุ่ม (i) แทน input เพราะ `<button>` เป็น
                  labelable element ที่มาก่อนตาม DOM order */}
              <InfoHint
                label="เกี่ยวกับรหัสสินค้า"
                text="รหัสที่ผู้ใช้เห็นและค้นหา ต้องไม่ซ้ำทั้งระบบ"
              />
            </label>
            <label className="cap-field">
              <span className="cap-field__label">ชื่อสินค้า (name)</span>
              <input
                className="cap-input"
                name="name"
                value={form.name}
                onChange={(event) => update('name', event.target.value)}
                required
              />
            </label>
            <label className="cap-field">
              <span className="cap-field__label">รุ่น/โมเดล (model)</span>
              <input
                className="cap-input"
                name="model"
                data-testid="product-create-model"
                value={form.model}
                onChange={(event) => update('model', event.target.value)}
              />
              <InfoHint
                label="เกี่ยวกับรุ่น/โมเดล"
                text="ไม่บังคับ — ข้อความอิสระตามที่ลูกค้ากำหนด สินค้าส่วนใหญ่ไม่มีรุ่น/โมเดล"
              />
            </label>

            <div className="cap-field">
              <label className="cap-field__label" htmlFor="product-create-product-type">
                ประเภทสินค้า (product_type)
              </label>
              <div className="cap-field__add-row">
                <select
                  id="product-create-product-type"
                  className="cap-input"
                  name="product_type"
                  data-testid="product-create-product-type"
                  value={form.product_type}
                  disabled={productTypes.status === 'loading'}
                  onChange={(event) => update('product_type', event.target.value)}
                  required
                >
                  <option value="">เลือกประเภทสินค้า</option>
                  {productTypes.status === 'ready'
                    ? productTypes.options.map((option) => (
                        <option key={option.code} value={option.code}>
                          {option.code} — {option.name}
                        </option>
                      ))
                    : null}
                </select>
                <button
                  type="button"
                  className="cap-button cap-button--ghost cap-button--icon"
                  data-testid="product-create-product-type-add"
                  aria-label="เพิ่มหรือจัดการประเภทสินค้า"
                  onClick={() => setRegisterModalKind('type')}
                >
                  +
                </button>
              </div>
              {productTypes.status === 'loading' || productTypes.status === 'ready' ? null : (
                <span
                  className="cap-field__hint cap-field__hint--error"
                  data-testid="product-create-product-type-load-failure"
                >
                  โหลดรายการประเภทสินค้าไม่สำเร็จ{' '}
                  <button
                    type="button"
                    className="cap-button cap-button--ghost"
                    data-testid="product-create-product-type-retry"
                    onClick={() => setProductTypesReloadToken((token) => token + 1)}
                  >
                    ลองใหม่
                  </button>
                </span>
              )}
            </div>

            <div className="cap-field">
              <label className="cap-field__label" htmlFor="product-create-base-uom">
                หน่วยนับหลัก (base_uom){' '}
                <InfoHint
                  label="เกี่ยวกับหน่วยนับหลัก"
                  text="เฟส 1 มีหน่วยนับหลักเดียวต่อสินค้า"
                />
              </label>
              <div className="cap-field__add-row">
                <select
                  id="product-create-base-uom"
                  className="cap-input"
                  name="base_uom"
                  data-testid="product-create-base-uom"
                  value={form.base_uom}
                  disabled={uoms.status === 'loading'}
                  onChange={(event) => update('base_uom', event.target.value)}
                  required
                >
                  <option value="">เลือกหน่วยนับหลัก</option>
                  {uoms.status === 'ready'
                    ? uoms.options.map((option) => (
                        <option key={option.code} value={option.code}>
                          {option.code} — {option.name}
                        </option>
                      ))
                    : null}
                </select>
                <button
                  type="button"
                  className="cap-button cap-button--ghost cap-button--icon"
                  data-testid="product-create-base-uom-add"
                  aria-label="เพิ่มหรือจัดการหน่วยนับ"
                  onClick={() => setRegisterModalKind('uom')}
                >
                  +
                </button>
              </div>
              {uoms.status === 'loading' || uoms.status === 'ready' ? null : (
                <span
                  className="cap-field__hint cap-field__hint--error"
                  data-testid="product-create-base-uom-load-failure"
                >
                  โหลดรายการหน่วยนับไม่สำเร็จ{' '}
                  <button
                    type="button"
                    className="cap-button cap-button--ghost"
                    data-testid="product-create-base-uom-retry"
                    onClick={() => setUomsReloadToken((token) => token + 1)}
                  >
                    ลองใหม่
                  </button>
                </span>
              )}
            </div>
          </div>
          <ProductSteelFields idPrefix="product-create" value={form.steel} onChange={updateSteel} />
        </section>

        <section className="cap-card cap-card--form" aria-label="ข้อมูลระดับโรงงาน">
          <div className="cap-card__head">
            <h2 className="cap-card__title">
              ข้อมูลระดับโรงงาน
              <InfoHint
                label="เกี่ยวกับข้อมูลระดับโรงงาน"
                text="ต้องมีอย่างน้อยหนึ่งโรงงาน และต้องเป็นโรงงานที่คุณมีสิทธิ์เข้าถึง"
              />
            </h2>
          </div>
          <div className="cap-form__grid">
            <label className="cap-field">
              <span className="cap-field__label">รหัสโรงงาน (plant_code)</span>
              <select
                className="cap-input"
                name="plant_code"
                data-testid="product-create-plant-code"
                value={form.plant_code}
                onChange={(event) => update('plant_code', event.target.value)}
                required
              >
                {plants.length === 1 ? null : (
                  <option value="">
                    {plants.length === 0 ? '— ไม่มีโรงงานที่คุณเห็น —' : 'เลือกโรงงาน'}
                  </option>
                )}
                {plants.map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </select>
              {/* ไม่มีปุ่ม '+' ที่นี่: ระบบไม่มีเส้นสร้างโรงงาน โรงงานเป็นข้อมูลตั้งต้นของระบบ ไม่ใช่ทะเบียนที่แก้จากหน้าจอนี้ (D-135 ข้อ 3) */}
            </label>
            <label className="cap-field">
              <span className="cap-field__label">โหมดเติมเต็ม (default_replenishment_mode)</span>
              <select className="cap-input" name="default_replenishment_mode" value="PUSH" disabled>
                {CREATABLE_REPLENISHMENT_MODES.map((mode) => (
                  <option key={mode} value={mode}>
                    {mode}
                  </option>
                ))}
              </select>
              <InfoHint
                label="เกี่ยวกับโหมดเติมเต็ม"
                text={
                  <span data-testid="product-create-push-note">
                    เฟส 1 ผลิตแบบ PUSH เท่านั้น (D-034) — ยังไม่มีตัวเลือก Pull ให้สร้าง
                  </span>
                }
              />
            </label>
            <label className="cap-field">
              <span className="cap-field__label">Valuation class</span>
              <input
                className="cap-input"
                name="valuation_class"
                value={form.valuation_class}
                onChange={(event) => update('valuation_class', event.target.value)}
                placeholder="RAW_MATERIAL"
              />
            </label>
            <label className="cap-field">
              <span className="cap-field__label">Lead time (วัน)</span>
              <input
                className="cap-input"
                type="number"
                name="planned_delivery_time"
                min={0}
                step={1}
                inputMode="numeric"
                value={form.planned_delivery_time}
                onChange={(event) => update('planned_delivery_time', event.target.value)}
                placeholder="7"
              />
            </label>
            <label className="cap-field">
              <span className="cap-field__label">วิธีกำหนดขนาดล็อต</span>
              <input
                className="cap-input"
                name="lot_size_procedure"
                value={form.lot_size_procedure}
                onChange={(event) => update('lot_size_procedure', event.target.value)}
              />
            </label>
            <label className="cap-field">
              <span className="cap-field__label">สต๊อกปลอดภัย (safety_stock)</span>
              <input
                className="cap-input"
                type="number"
                name="safety_stock"
                min={0}
                step="any"
                inputMode="decimal"
                value={form.safety_stock}
                onChange={(event) => update('safety_stock', event.target.value)}
                placeholder="100.0000"
              />
            </label>
            <label className="cap-field">
              <span className="cap-field__label">ต้นทุนต่อหน่วย (unit_cost)</span>
              <input
                className="cap-input"
                type="number"
                name="unit_cost"
                min={0}
                step="any"
                inputMode="decimal"
                value={form.unit_cost}
                onChange={(event) => update('unit_cost', event.target.value)}
                placeholder="42.5000"
              />
              <InfoHint
                label="เกี่ยวกับการส่งค่าต้นทุนต่อหน่วย"
                text="ส่งเป็นข้อความตามที่พิมพ์ทุกตัวอักษร เพื่อไม่ให้ทศนิยมของ NUMERIC(18,4) หายไป"
              />
            </label>
          </div>
        </section>

        <div className="cap-form__actions">
          <button
            type="submit"
            className="cap-button cap-button--primary"
            data-testid="product-create-submit"
            disabled={submit.status === 'submitting'}
          >
            {submit.status === 'submitting' ? 'กำลังบันทึก…' : 'บันทึกเป็นร่าง'}
          </button>
          <InfoHint
            label="เกี่ยวกับฟิลด์ที่เซิร์ฟเวอร์เติมให้"
            text="สถานะ ผู้สร้าง และผู้อนุมัติ ถูกเติมโดยเซิร์ฟเวอร์ — ฟอร์มนี้ไม่มีช่องให้ระบุและไม่ส่งค่าเหล่านั้น"
          />
        </div>
      </form>

      <ProductRegisterModal
        kind={registerModalKind ?? 'type'}
        open={registerModalKind !== null}
        onClose={() => setRegisterModalKind(null)}
        permissions={permissions}
        apiBaseUrl={apiBaseUrl}
        fetchImpl={fetchImpl}
        onRegisterChanged={handleRegisterChanged}
        onCreated={handleRegisterCreated}
      />
    </div>
  );
}
